// src/components/UsageSummary.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { format, parseISO, subDays } from 'date-fns';
import { API_BASE_URL } from '../config';

/* ───────── helpers: csv + pagination ───────── */
function useDebouncedValue(value, delay = 2000) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function downloadCsv({ headers, rows, filename }) {
  const esc = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = headers.map(h => esc(h.label)).join(',');
  const body = rows.map(row =>
    headers.map(h => {
      const v = h.get ? h.get(row) : (row?.[h.key] ?? row?.featureUsageCounts?.[h.key]);
      return esc(v);
    }).join(',')
  );
  const csv = [head, ...body].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function pageSlice(items, page, pageSize) {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

function Pagination({ page, pageSize, total, onChange }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 1, canNext = page < pageCount;
  const bar = { display:'flex', gap:8, alignItems:'center', justifyContent:'space-between', padding:12, borderTop:'1px solid #e5e7eb', background:'#fff' };
  const btn = (ok)=>({ padding:'6px 10px', border:'1px solid #e5e7eb', background:ok?'#fff':'#f3f4f6', borderRadius:8, cursor:ok?'pointer':'not-allowed', fontSize:12 });
  const meter = { fontSize:12, color:'#64748b' };
  const select = { padding:'6px 8px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:12 };
  return (
    <div style={bar}>
      <span style={meter}>Page {page} / {pageCount} • {total.toLocaleString()} rows</span>
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <select value={pageSize} onChange={(e)=>onChange({ page:1, pageSize:Number(e.target.value) })} style={select}>
          {[10,25,50,100,250,500].map(n=><option key={n} value={n}>{n} / page</option>)}
        </select>
        <button onClick={()=>onChange({ page:1 })} disabled={!canPrev} style={btn(canPrev)} title="First">«</button>
        <button onClick={()=>onChange({ page:page-1 })} disabled={!canPrev} style={btn(canPrev)} title="Prev">‹</button>
        <button onClick={()=>onChange({ page:page+1 })} disabled={!canNext} style={btn(canNext)} title="Next">›</button>
        <button onClick={()=>onChange({ page:pageCount })} disabled={!canNext} style={btn(canNext)} title="Last">»</button>
      </div>
    </div>
  );
}

/* ───────── tiny MultiSelect Combo component ───────── */
function MultiSelectCombo({ options, value, onChange, placeholder = 'Select features…' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const lowerQ = q.toLowerCase();
  const filtered = options.filter(o => o.toLowerCase().includes(lowerQ));

  const tagWrap = { display:'flex', flexWrap:'wrap', gap:6, minHeight:34, alignItems:'center' };
  const tag = { background:'#eef2ff', color:'#3730a3', padding:'2px 8px', borderRadius:999, fontSize:12 };
  const chipBtn = { marginLeft:6, cursor:'pointer', border:'none', background:'transparent', color:'#6b7280' };

  return (
    <div ref={ref} style={{ position:'relative', minWidth:260 }}>
      <div
        onClick={() => setOpen(v => !v)}
        style={{ padding:'8px 10px', borderRadius:10, border:'1px solid #e5e7eb', background:'#fff', fontSize:12, cursor:'text' }}
      >
        {value.length === 0 ? (
          <span style={{ color:'#94a3b8' }}>{placeholder}</span>
        ) : (
          <div style={tagWrap}>
            {value.map(v => (
              <span key={v} style={tag}>
                {v}
                <button
                  type="button"
                  style={chipBtn}
                  onClick={(e) => { e.stopPropagation(); onChange(value.filter(x => x !== v)); }}
                  title="Remove"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {open && (
        <div style={{
          position:'absolute', zIndex:10, top:'100%', left:0, right:0,
          background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, marginTop:6, boxShadow:'0 10px 14px rgba(0,0,0,.06)'
        }}>
          <div style={{ padding:8, borderBottom:'1px solid #e5e7eb' }}>
            <input
              autoFocus
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              placeholder="Search…"
              style={{ width:'100%', padding:'8px 10px', borderRadius:8, border:'1px solid #e5e7eb', fontSize:12 }}
            />
          </div>
          <div style={{ maxHeight:260, overflow:'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding:10, fontSize:12, color:'#94a3b8' }}>No matches</div>
            ) : filtered.map(opt => {
              const checked = value.includes(opt);
              return (
                <label key={opt} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', cursor:'pointer' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const next = checked ? value.filter(v => v !== opt) : [...value, opt];
                      onChange(next);
                    }}
                  />
                  <span style={{ fontSize:12 }}>{opt}</span>
                </label>
              );
            })}
          </div>
          <div style={{ display:'flex', gap:8, padding:8, borderTop:'1px solid #e5e7eb', justifyContent:'space-between' }}>
            <button type="button" onClick={() => onChange(options)} style={{ fontSize:12, border:'1px solid #e5e7eb', padding:'6px 8px', borderRadius:8, background:'#fff' }}>Select all</button>
            <div style={{ display:'flex', gap:8 }}>
              <button type="button" onClick={() => onChange([])} style={{ fontSize:12, border:'1px solid #e5e7eb', padding:'6px 8px', borderRadius:8, background:'#fff' }}>Clear</button>
              <button type="button" onClick={() => setOpen(false)} style={{ fontSize:12, border:'1px solid #111827', padding:'6px 10px', borderRadius:8, background:'#111827', color:'#fff' }}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────── component ───────── */

export default function UsageSummary() {
  const ui = useMemo(() => ({
    bg:'#f8fafc', card:'#fff', text:'#0f172a', sub:'#64748b', border:'#e5e7eb', black:'#0b0f19', head:'#e6ebf1'
  }), []);

  // Filters
  const [filters, setFilters] = useState({
    userEmail: '',
    licenceId: '',
    companyId: '',
    projectId: '',
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  });

  const debouncedFilters = useDebouncedValue(filters, 2000);

  // NEW: multi-select features that control visible columns (hide others)
  const [selectedFeatures, setSelectedFeatures] = useState([]); // array of feature keys

  // Data
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // Sorting + layout
  const [sortConfig, setSortConfig] = useState({ key: 'userEmail', direction: 'asc' });
  const [arrangement, setArrangement] = useState('totals'); // 'totals' | 'alpha'

  // Default remains FALSE
  const [showIds, setShowIds] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  useEffect(()=>{ setPage(1); }, [filters, sortConfig, arrangement, showIds, selectedFeatures]);

  // Fetch
  const fetchSummaryData = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { userEmail, startDate, endDate, ...rest } = debouncedFilters;
      const params = { ...rest };
      if (startDate) params.afterDate = startDate;
      if (endDate) params.beforeDate = endDate;

      const res = await axios.get(`${API_BASE_URL}/api/usageSummary`, { params });
      const payload = res.data;
      let items = [];
      if (Array.isArray(payload)) items = payload;
      else {
        const guesses = ['usageSummary','userSummaries','data','items','summary','results'];
        for (const k of guesses) if (Array.isArray(payload?.[k])) { items = payload[k]; break; }
      }
      setSummaryData(Array.isArray(items) ? items : []);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || 'Failed to load usage summary');
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  useEffect(()=>{ fetchSummaryData(); }, [fetchSummaryData]);

  /* Build feature key universe + order */
  const featureKeys = useMemo(() => {
    const s = new Set();
    for (const item of summaryData) Object.keys(item?.featureUsageCounts || {}).forEach(k => s.add(k));
    let arr = Array.from(s);
    if (arrangement === 'alpha') arr.sort((a,b)=>a.localeCompare(b));
    if (arrangement === 'totals') {
      const totals = Object.fromEntries(arr.map(k => [k, 0]));
      for (const row of summaryData) {
        const f = row?.featureUsageCounts || {};
        for (const k of arr) totals[k] += Number(f[k] || 0);
      }
      arr.sort((a,b)=> (totals[b]||0) - (totals[a]||0));
    }
    return arr;
  }, [summaryData, arrangement]);

  /* Visible feature columns: only selected, fallback to all if none picked */
  const visibleFeatureKeys = useMemo(() => {
    return (selectedFeatures && selectedFeatures.length > 0)
      ? featureKeys.filter(k => selectedFeatures.includes(k))
      : featureKeys;
  }, [featureKeys, selectedFeatures]);

  /* Basic row filtering by IDs/email/time only (no feature-based row filtering now) */
  const filtered = useMemo(() => {
    const eFilter = (filters.userEmail || '').toLowerCase();
    const start = filters.startDate ? new Date(filters.startDate + 'T00:00:00') : null;
    const end = filters.endDate ? new Date(filters.endDate + 'T23:59:59') : null;

    return summaryData.filter(r => {
      if (eFilter && !(r.userEmail || '').toLowerCase().includes(eFilter)) return false;
      if (filters.licenceId && !String(r.licenceId||'').includes(filters.licenceId)) return false;
      if (filters.companyId && !String(r.companyId||'').includes(filters.companyId)) return false;
      if (filters.projectId && !String(r.projectId||'').includes(filters.projectId)) return false;

      const a = r.startDate ? new Date(r.startDate) : null;
      const b = r.endDate ? new Date(r.endDate) : null;
      if (start && a && a < start) return false;
      if (end && b && b > end) return false;
      return true;
    });
  }, [summaryData, filters]);

  // Sorting
  const sorted = useMemo(() => {
    const rows = [...filtered];
    const { key, direction } = sortConfig;
    rows.sort((a, b) => {
      const av = a?.[key] ?? a?.featureUsageCounts?.[key] ?? '';
      const bv = b?.[key] ?? b?.featureUsageCounts?.[key] ?? '';
      const na = Number(av), nb = Number(bv);
      let cmp;
      if (!Number.isNaN(na) && !Number.isNaN(nb)) cmp = na - nb;
      else cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base', numeric: true });
      return direction === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [filtered, sortConfig]);

  const currentPageRows = useMemo(() => pageSlice(sorted, page, pageSize), [sorted, page, pageSize]);

  // Headers (base + visible feature columns only)
  const baseHeaders = useMemo(() => {
    const arr = [
      { key: 'userEmail', label: 'User Email' },
      { key: 'startDate', label: 'Start' },
      { key: 'endDate', label: 'End' },
    ];
    if (showIds) {
      arr.push({ key: 'licenceId', label: 'Licence ID' });
      arr.push({ key: 'companyId', label: 'Company ID' });
      arr.push({ key: 'projectId', label: 'Project ID' });
    }
    return arr;
  }, [showIds]);

  const featureHeaders = useMemo(() => visibleFeatureKeys.map(k => ({ key:k, label:k })), [visibleFeatureKeys]);
  const headers = useMemo(() => [...baseHeaders, ...featureHeaders], [baseHeaders, featureHeaders]);

  // CSV exports: only visible feature columns
  const exportCurrent = () => downloadCsv({ headers, rows: currentPageRows, filename: `usage_summary_page-${page}_${format(new Date(), 'yyyyMMdd')}.csv` });
  const exportAll = () => {const rows = Array.isArray(summaryData) ? summaryData : []; 
    downloadCsv({headers, rows, filename: `usage_summary_all_${format(new Date(), 'yyyyMMdd')}.csv`,});
  };

  /* ───────── UI styles ───────── */
  const shell = { background: ui.bg, minHeight: '100%', padding: 16 };
  const card = { background: ui.card, border: `1px solid ${ui.border}`, borderRadius: 12, padding: 12, marginBottom: 12 };
  const btn = { padding:'8px 12px', borderRadius:10, fontSize:12, fontWeight:700, cursor:'pointer' };
  const btnGhost = { ...btn, background:'#fff', color:ui.text, border:`1px solid ${ui.border}` };
  const btnPrimary = { ...btn, background:ui.black, color:'#fff', border:`1px solid ${ui.black}` };
  const input = { padding:'8px 10px', borderRadius:10, border:`1px solid ${ui.border}`, background:'#fff', fontSize:12, outline:'none' };
  const labelStyle = { fontSize:12, color:ui.sub };

  return (
    <div style={shell}>
      {/* Title */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <div>
          <div style={{ fontSize:22, fontWeight:800, color: ui.text }}>Usage Summary</div>
          <div style={{ fontSize:13, color: ui.sub }}>Pick features in the combo to show only those columns.</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={exportCurrent} style={btnGhost}>Export current page</button>
          <button onClick={exportAll} style={btnPrimary}>Export all (unfiltered)</button>
        </div>
      </div>

      {/* Filters */}
      <section style={card}>
        <div style={{ display:'flex', flexWrap:'wrap', gap:8, alignItems:'center' }}>
          <label style={labelStyle}>
            Start
            <input type="date" value={filters.startDate} onChange={e=>setFilters(f=>({ ...f, startDate:e.target.value }))} style={{ ...input, marginLeft:6 }} />
          </label>
          <label style={labelStyle}>
            End
            <input type="date" value={filters.endDate} max={format(new Date(), 'yyyy-MM-dd')} onChange={e=>setFilters(f=>({ ...f, endDate:e.target.value }))} style={{ ...input, marginLeft:6 }} />
          </label>
          <label style={labelStyle}>
            Email
            <input type="text" placeholder="user@company.com" value={filters.userEmail} onChange={e=>setFilters(f=>({ ...f, userEmail:e.target.value }))} style={{ ...input, marginLeft:6, minWidth:220 }} />
          </label>
          <label style={labelStyle}>
            Licence ID
            <input type="text" value={filters.licenceId} onChange={e=>setFilters(f=>({ ...f, licenceId:e.target.value }))} style={{ ...input, marginLeft:6, width:140 }} />
          </label>
          <label style={labelStyle}>
            Company ID
            <input type="text" value={filters.companyId} onChange={e=>setFilters(f=>({ ...f, companyId:e.target.value }))} style={{ ...input, marginLeft:6, width:140 }} />
          </label>
          <label style={labelStyle}>
            Project ID
            <input type="text" value={filters.projectId} onChange={e=>setFilters(f=>({ ...f, projectId:e.target.value }))} style={{ ...input, marginLeft:6, width:140 }} />
          </label>

          <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
            <button onClick={()=>{ fetchSummaryData(); }} style={btnGhost}>Refresh</button>
            <button
              onClick={()=>{
                setFilters({
                  userEmail:'', licenceId:'', companyId:'', projectId:'',
                  startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
                  endDate: format(new Date(), 'yyyy-MM-dd')
                });
                setSelectedFeatures([]);
                setShowIds(false);
              }}
              style={btnGhost}
            >
              Clear
            </button>
          </div>
        </div>

        {/* View options */}
        <div style={{ display:'flex', gap:12, alignItems:'center', marginTop:10, flexWrap:'wrap' }}>
          <label style={labelStyle}>
            Arrangement
            <select
              value={arrangement}
              onChange={(e)=>setArrangement(e.target.value)}
              style={{ ...input, marginLeft:6, padding:'6px 10px' }}
            >
              <option value="totals">By totals</option>
              <option value="alpha">A–Z</option>
            </select>
          </label>

          {/* Features combo sits here, between Arrangement and Show IDs */}
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <span style={labelStyle}>Features</span>
            <MultiSelectCombo
              options={featureKeys}
              value={selectedFeatures}
              onChange={setSelectedFeatures}
              placeholder="Select features…"
            />
          </div>

          <label style={{ ...labelStyle, display:'flex', alignItems:'center', gap:6 }}>
            <input type="checkbox" checked={showIds} onChange={e=>setShowIds(e.target.checked)} />
            Show IDs
          </label>
        </div>
      </section>

      {/* Table */}
      <section style={{ background: ui.card, border:`1px solid ${ui.border}`, borderRadius: 12, overflow:'hidden' }}>
        <div style={{ overflow:'auto' }}>
          <table style={{ fontSize:12, borderCollapse:'separate', borderSpacing:0, width:'100%' }}>
            <thead>
              <tr>
                {headers.map(h => {
                  const isBase = !!baseHeaders.find(b=>b.key===h.key);
                  return (
                    <th
                      key={h.key}
                      onClick={() => setSortConfig(s => ({ key:h.key, direction: s.key === h.key && s.direction === 'asc' ? 'desc' : 'asc' }))}
                      style={{
                        cursor:'pointer',
                        writingMode: isBase ? 'horizontal-tb' : 'vertical-rl',
                        transform: isBase ? 'none' : 'rotate(180deg)',
                        whiteSpace:'nowrap',
                        textAlign:'center',
                        padding:'4px',
                        minWidth: isBase ? '140px' : '30px',
                        background: ui.head,
                        position: h.key === 'userEmail' ? 'sticky' : 'static',
                        left: h.key === 'userEmail' ? 0 : undefined,
                        zIndex: h.key === 'userEmail' ? 3 : 1
                      }}
                      title={isBase ? h.label : `${h.label} (click to sort)`}
                    >
                      {h.label} {sortConfig.key === h.key ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={headers.length} style={{ padding:16, color: ui.sub }}>Loading…</td></tr>
              ) : err ? (
                <tr><td colSpan={headers.length} style={{ padding:16, color:'#b91c1c' }}>{err}</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={headers.length} style={{ padding:16, color: ui.sub }}>No data found.</td></tr>
              ) : (
                currentPageRows.map((row, idx) => (
                  <tr key={(row.userEmail || 'row') + '_' + idx}>
                    {headers.map((h) => (
                      <td
                        key={h.key}
                        style={{
                          borderBottom:`1px solid ${ui.border}`,
                          padding:'8px 10px',
                          background:'#fff',
                          position: h.key === 'userEmail' ? 'sticky' : 'static',
                          left: h.key === 'userEmail' ? 0 : undefined,
                          zIndex: h.key === 'userEmail' ? 2 : 1
                        }}
                        title={String(row?.[h.key] ?? row?.featureUsageCounts?.[h.key] ?? '')}
                      >
                        {renderCell(row, h)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          pageSize={pageSize}
          total={sorted.length}
          onChange={({ page: p = page, pageSize: ps = pageSize }) => { setPage(p); setPageSize(ps); }}
        />
      </section>
    </div>
  );
}

function renderCell(row, h) {
  const val = row?.[h.key] ?? row?.featureUsageCounts?.[h.key];
  if (/date/i.test(h.key) && val) {
    try { return format(parseISO(String(val)), 'yyyy-MM-dd'); } catch { return String(val); }
  }
  if (typeof val === 'object' && val !== null) return JSON.stringify(val);
  if (val === undefined || val === null || val === '') return '—';
  return String(val);
}
